import React, { useMemo } from 'react';
import { AccountingSystemProviders } from '@shared/utils/constants';
import type { GetConnectionAndMappingResponse } from '@api/features/accounting-system/getConnectionAndMapping';
import type { GetSuppliersResponse } from '@api/features/suppliers/getSuppliers';
import { SupplierProps } from '@api/models/supplier';
import EditableTable from '@client/components/EditableTable/EditableTable';
import Card from '@client/components/Card/Card';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import {
	createMappingEntry,
	getAccountingCustomers,
	getAccountingSuppliers,
	getSuppliers,
} from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import showSuccessNotification from '@client/utils/showSuccessNotification';

const D365_PROVIDERS = [AccountingSystemProviders['365bc'], AccountingSystemProviders['365fo']];

type SupplierRow = SupplierProps & {
	vendorCode: string | undefined;
	customerCode: string | undefined;
};

const SupplierMappingTab = ({
	connection,
	refreshConnection,
}: {
	connection: GetConnectionAndMappingResponse | undefined;
	refreshConnection: () => void;
}) => {
	const [suppliers, refreshSuppliers] = useFetchedState(getSuppliers);

	const isD365 = connection?.provider != null && D365_PROVIDERS.includes(connection.provider as any);

	const [vendors] = useFetchedState(async () => (
		connection == null ? [] : getAccountingSuppliers(connection.id)
	), [connection]);

	const [customers] = useFetchedState(async () => (
		connection == null || !isD365 ? [] : getAccountingCustomers(connection.id)
	), [connection, isD365]);

	const suppliersList = useMemo((): SupplierRow[] => {
		if (suppliers == null || connection == null) {
			return [];
		}

		return suppliers.map((s) => {
			const vendorEntry = connection.AccountingMappingEntries.find((m) => (
				m.type === 'supplier' && m.cvIdentifier === String(s.id)
			));
			const customerEntry = connection.AccountingMappingEntries.find((m) => (
				m.type === 'supplierCustomer' && m.cvIdentifier === String(s.id)
			));

			return {
				...s,
				vendorCode: vendorEntry?.code ?? undefined,
				customerCode: customerEntry?.code ?? undefined,
			};
		});
	}, [suppliers, connection]);

	const onSave = async (
		supplierId: number | undefined,
		update: Partial<GetSuppliersResponse[number] & { vendorCode: string; customerCode: string }>,
	) => {
		if (supplierId == null || connection == null) {
			refreshSuppliers();

			return;
		}

		const currentRow = suppliersList.find((s) => s.id === supplierId);

		if (currentRow == null) {
			refreshSuppliers();

			return;
		}

		let didSave = false;

		if ('vendorCode' in update && update.vendorCode !== currentRow.vendorCode) {
			try {
				await createMappingEntry('supplier', supplierId, update.vendorCode, connection.id);
				didSave = true;
			} catch (e) {
				showErrorNotification('Could not save supplier code mapping', e as Error);
			}
		}

		if (isD365 && 'customerCode' in update && update.customerCode !== currentRow.customerCode) {
			try {
				await createMappingEntry('supplierCustomer', supplierId, update.customerCode, connection.id);
				didSave = true;
			} catch (e) {
				showErrorNotification('Could not save customer code mapping', e as Error);
			}
		}

		if (didSave) {
			await refreshConnection();
			showSuccessNotification('Successfully mapped entry');
		}

		refreshSuppliers();
	};

	return (
		<Card slim>
			<EditableTable<SupplierRow, 'id'>
				actionsTitle=""
				iconButtons
				pagination={false}
				keyDataIndex="id"
				onSave={onSave}
				dataSource={suppliersList}
				columns={[
					{
						dataIndex: 'id',
						title: 'ID',
					},
					{
						dataIndex: 'name',
						title: 'Name',
					},
					{
						dataIndex: 'vendorCode',
						title: isD365 ? 'Supplier code' : 'Accounting System Entry',
						editingProps: {
							type: 'select',
							options: (vendors ?? []).map((v) => ({
								label: v.name,
								value: v.id,
							})),
							inputProps: {
								placeholder: 'Select supplier',
								showSearch: true,
								allowClear: true,
							},
						},
						editable: true,
						render: (v: string | null) => (
							v == null ? (<i>Empty</i>) : vendors?.find((s) => s.id === v)?.name
						),
					},
					...(isD365 ? [{
						dataIndex: 'customerCode' as const,
						title: 'Customer code',
						editingProps: {
							type: 'select' as const,
							options: (customers ?? []).map((c) => ({
								label: c.name,
								value: c.id,
							})),
							inputProps: {
								placeholder: 'Select customer',
								showSearch: true,
								allowClear: true,
							},
						},
						editable: true,
						render: (v: string | null) => (
							v == null ? (<i>Empty</i>) : customers?.find((c) => c.id === v)?.name
						),
					}] : []),
				]}
			/>
		</Card>
	);
};

export default SupplierMappingTab;
